import { ChangeLog, Response, apiAbortController } from '@ui/src';
import { Balance } from '../../statements/types';
import api from '../../../api/api';
import {
  Account,
  AccountsStats,
  AccountsValidatorResponse,
  AccountFormValues,
} from '../types';

export const accountsListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAccountsValidators: build.query<AccountsValidatorResponse, void>({
      query: () => ({
        url: '/v1/accounts/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsList: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccount: build.query<
      { data: Account },
      { id: string; closingBalancesAt?: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}${
          data?.closingBalancesAt
            ? `?closingBalancesAt=${data?.closingBalancesAt}`
            : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsStats: build.query<{ data: AccountsStats[] }, void>({
      query: () => ({
        url: '/v1/accounts-stats',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsBalances: build.query<{ data: Balance[] }, string>({
      query: (query) => ({
        url: `/v1/balances/closing-balances${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountClosingBalances: build.query<{ data: Balance[] }, string>({
      query: (query) => ({
        url: `/v1/balances/closing-balances${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createAccount: build.mutation<
      {
        data: Account;
      },
      AccountFormValues
    >({
      query: (body) => ({
        url: '/v1/accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    blockCredit: build.mutation<
      {
        data: Account;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/accounts/${id}/block-credit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    unblockCredit: build.mutation<
      {
        data: Account;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/accounts/${id}/unblock-credit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    blockDebit: build.mutation<
      {
        data: Account;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/accounts/${id}/block-debit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    unblockDebit: build.mutation<
      {
        data: Account;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/accounts/${id}/unblock-debit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    closeAccount: build.mutation<
      {
        data: Account;
      },
      { body: { comment: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/close`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    openAccount: build.mutation<
      {
        data: Account;
      },
      { body: { comment: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/open`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateAccount: build.mutation<
      {
        data: Account;
      },
      {
        id: string;
        closed?: boolean;
        revaluable?: boolean;
        description?: string;
        creditBlock?: boolean;
        debitBlock?: boolean;
      }
    >({
      query: (body) => ({
        url: `/v1/accounts/${body.id}`,
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateOverdraftStatus: build.mutation<
      {
        data: Account;
      },
      {
        id: string;
        overdraftEnabled: boolean;
      }
    >({
      query: (body) => ({
        url: `/v1/accounts/${body.id}/overdraft`,
        method: 'PUT',
        body: { overdraftEnabled: body.overdraftEnabled },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateOverdraftBalanceValue: build.mutation<
      {
        data: Balance;
      },
      {
        id: string;
        balanceId: string;
        overdraftEnabled: boolean;
        overdraftValue: number;
      }
    >({
      query: (body) => ({
        url: `/v1/accounts/${body.id}/balances/${body.balanceId}/overdraft`,
        method: 'PUT',
        body: {
          overdraftValue: body.overdraftValue,
          overdraftEnabled: body.overdraftEnabled,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountBalances: build.query<Response<Balance>, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeAccountBalanceStatus: build.mutation<
      { data: Account },
      { id: string; body: { currencyId: string; active: boolean } }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportAccounts: build.mutation<
      any,
      Record<string, string[] | string | number>
    >({
      query: (body) => ({
        url: '/v1/accounts/export',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetAccountsListQuery,
  useGetAccountsValidatorsQuery,
  useLazyGetAccountsStatsQuery,
  useCreateAccountMutation,
  useLazyGetAccountQuery,
  useLazyGetAccountBalancesQuery,
  useUpdateAccountMutation,
  useBlockCreditMutation,
  useBlockDebitMutation,
  useUnblockCreditMutation,
  useUnblockDebitMutation,
  useCloseAccountMutation,
  useOpenAccountMutation,
  useChangeAccountBalanceStatusMutation,
  useExportAccountsMutation,
  useUpdateOverdraftStatusMutation,
  useUpdateOverdraftBalanceValueMutation,
  useLazyGetAccountChangeLogsQuery,
  useLazyGetAccountsBalancesQuery,
  useLazyGetAccountClosingBalancesQuery,
} = accountsListApi;

export const {
  endpoints: {
    getAccountsList,
    getAccount,
    getAccountsValidators,
    getAccountsStats,
    createAccount,
    getAccountBalances,
    updateAccount,
    blockCredit,
    blockDebit,
    unblockCredit,
    unblockDebit,
    closeAccount,
    openAccount,
    changeAccountBalanceStatus,
    exportAccounts,
    updateOverdraftStatus,
    updateOverdraftBalanceValue,
    getAccountsBalances,
    getAccountClosingBalances,
  },
} = accountsListApi;
